import { dropPageLogs } from '../../../../widget/src/capture/pageLogs';
import { API_BASE } from '../../lib/apiClient';
import { DOGFOOD_WIDGET_KEY } from './dogfoodKey';

/**
 * Toado's own widget on the Toado app, heard from before the app starts and
 * shown only to staff (TOADO-910).
 *
 * The widget hears the page's console and errors from the moment its listeners
 * start. Started after React mounted and sign-in resolved, it missed everything
 * the app logged and threw while it started, which is most of what a report on
 * the app needs. So:
 *
 * - `earlyDogfoodWidget.ts`, which index.html runs before the app, starts the
 *   listeners on a browser where the last person to sign in was staff and has
 *   not signed out (`staffRemembered`), and in a dev build. Listening only: no
 *   loader, no launcher, nothing sent.
 * - Once auth resolves, DogfoodWidget puts the loader on the page for staff,
 *   which keeps those listeners, or drops them for anyone else (`removeDogfoodWidget`).
 *
 * A customer's browser has never had a staff sign-in, so customers never get
 * the collectors (the reason the widget is staff-or-dev only), and a browser a
 * staff member used gets them only as far as listening, until auth says who it
 * is. The loader is the same file a customer's page includes.
 */

export const DOGFOOD_SCRIPT_ID = 'toado-dogfood-widget';

/**
 * Set while the last sign-in on this browser was staff. A `vd-` key, so the
 * sign-out that clears every `vd-` key clears it too (Codex, round 1): a staff
 * member who signs out leaves no early start for whoever uses the browser next.
 */
const STAFF_FLAG = 'vd-dogfood-staff';

export function staffRemembered(): boolean {
  try { return localStorage.getItem(STAFF_FLAG) === '1'; } catch { return false; }
}

/** Remember who signed in, so the next page load can start listening before anyone has. */
export function rememberStaff(isStaff: boolean): void {
  try {
    if (isStaff) localStorage.setItem(STAFF_FLAG, '1');
    else localStorage.removeItem(STAFF_FLAG);
  } catch { /* storage refused: the widget starts after sign-in instead */ }
}

/**
 * Put the widget's loader on the page, once. Called only for staff, so it is
 * what lifts a removal's cancellation on this page.
 */
export function injectDogfoodWidget(): void {
  if (document.getElementById(DOGFOOD_SCRIPT_ID)) return;
  const page = window as unknown as ToadoWindow;
  if (page.__toadoWidgetCancelled) {
    // What a removal left: its mark, and, when it came before the bundle, the
    // loader's guard and its stub, which would stop this loader from running
    // at all (z.ai, round 5). A bundle of the old one still in flight then
    // boots, as it may now that staff are signed in; this one's stops at its
    // guard.
    try {
      delete page.__toadoWidgetCancelled;
      delete page.__toadoLoaderRan;
      if (Array.isArray(page.Toado?.q)) delete page.Toado;
    } catch { /* our own window: these are always deletable */ }
  }
  const script = document.createElement('script');
  script.id = DOGFOOD_SCRIPT_ID;
  script.src = `${window.location.origin}/widget/v1/loader.js`;
  script.setAttribute('data-toado-key', DOGFOOD_WIDGET_KEY);
  // API_BASE is '' for same-origin deployments, so fall back to the app
  // origin: the SDK builds absolute URLs from it.
  script.setAttribute('data-toado-api', API_BASE || window.location.origin);
  (document.head ?? document.documentElement).appendChild(script);
}

type ToadoWindow = {
  Toado?: { destroy?: unknown; q?: Array<{ method: string; args: unknown[] }> };
  __toadoLoaderRan?: boolean | object;
  __toadoWidgetLoaded?: boolean;
  __toadoWidgetCancelled?: boolean;
};

/**
 * Take the widget off the page for a session that is not staff, at once:
 *
 * - listeners started early with no widget yet are dropped;
 * - a loader whose script has not run yet will not start (it sees its script
 *   is off the page); one that has run, with its bundle still in flight, has
 *   its listeners dropped and a destroy queued, so the bundle starts nothing;
 * - a widget that has booted is destroyed, and the page forgets it was ever
 *   loaded, so a staff sign-in later on the same page can put it back
 *   (Codex and z.ai, round 2). A bundle still in flight keeps its guards, so it
 *   cannot boot past its queued destroy; staff then get it on the next load.
 *
 * Either way the page is marked cancelled, and only `injectDogfoodWidget`, for
 * staff, lifts the mark. Guards alone did not hold: a bundle that failed to
 * load took the stub and its queued destroy with it, and forgetting a booted
 * widget let a second copy still in flight boot after the sign-out
 * (Codex, round 5).
 */
export function removeDogfoodWidget(): void {
  dropPageLogs();
  document.getElementById(DOGFOOD_SCRIPT_ID)?.remove();
  const page = window as unknown as ToadoWindow;
  page.__toadoWidgetCancelled = true;
  const toado = page.Toado;
  // The loader's stub first: it has a destroy of its own now (which queues
  // one), so a destroy function alone does not say the widget booted.
  if (Array.isArray(toado?.q)) {
    if (typeof toado.destroy === 'function') (toado.destroy as () => void)();
    else toado.q.push({ method: 'destroy', args: [] });
  } else if (typeof toado?.destroy === 'function') {
    (toado.destroy as () => void)();
    try {
      delete page.Toado;
      delete page.__toadoLoaderRan;
      delete page.__toadoWidgetLoaded;
    } catch { /* our own window: these are always deletable (z.ai, round 6) */ }
  }
}
